import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Divider,
  Stack,
  Chip,
  CircularProgress,
  IconButton
} from '@mui/joy';
import {
  GoogleLogo as GoogleIcon,
  CheckCircle as CheckIcon,
  XCircle as ErrorIcon,
  LinkSimple as LinkIcon,
  LinkBreak as LinkOffIcon,
  X as CloseIcon,
  MagnifyingGlass as SearchIcon,
  Buildings as BusinessIcon
} from '@phosphor-icons/react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { KeywordsManagement } from '../../components/marketing/KeywordsManagement';
import { CompetitorsManagement } from '../../components/marketing/CompetitorsManagement';
import { GBPLocationSelectModal } from '../../components/marketing/GBPLocationSelectModal';

interface GBPProfile {
  id: number;
  location_name: string;
  profile_id: string;
  connection_status?: 'location_linked' | 'location_required' | 'disconnected';
  address: string;
  phone: string;
  website: string;
  is_active: boolean;
  last_synced?: string;
}

export const MarketingSettings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showLocationSelectModal, setShowLocationSelectModal] = useState(false);
  const queryClient = useQueryClient();

  // Check for success and error messages in URL parameters
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const selectGbp = searchParams.get('select_gbp');

    // Handle multi-location selection flow
    if (selectGbp === 'true') {
      setShowLocationSelectModal(true);
      searchParams.delete('select_gbp');
      setSearchParams(searchParams, { replace: true });
    }

    if (success === 'gbp_connected') {
      setSuccessMessage('Google Business Profile connected successfully!');
      // Invalidate the query to refresh the profiles list
      queryClient.invalidateQueries({ queryKey: ['gbp-profiles'] });
      searchParams.delete('success');
      setSearchParams(searchParams, { replace: true });
    }
    
    if (error === 'oauth_not_configured') {
      setErrorMessage('Google Business Profile OAuth is not configured. Please contact your administrator to set up GBP_CLIENT_ID and GBP_CLIENT_SECRET.');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    } else if (error === 'session_expired') {
      setErrorMessage('Session expired. Please try connecting again.');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    } else if (error === 'oauth_error') {
      setErrorMessage('OAuth authorization failed. Please try again.');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    } else if (error === 'insufficient_scope') {
      setErrorMessage('Google Business Profile permission was not granted. Reconnect Google with the account that manages this business and allow access to manage your business listings.');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    } else if (error === 'location_required') {
      setErrorMessage('Google authorization was saved, but no business location was linked. Retry the connection below, or reconnect using the Google account that manages this business.');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    } else if (error === 'connection_failed') {
      setErrorMessage('Failed to connect Google Business Profile. Please try again.');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, queryClient]);

  // Fetch GBP profiles
  const { data: profiles = [], isLoading: profilesLoading } = useQuery({
    queryKey: ['gbp-profiles'],
    queryFn: async () => {
      try {
        const response = await api.get<{ results: GBPProfile[] }>('/api/marketing/gbp-profiles/');
        
        if (!response) {
          return [];
        }
        
        // Handle the response based on its structure
        if (Array.isArray(response)) {
          return response;
        } else if (response && typeof response === 'object') {
          if ('results' in response && Array.isArray(response.results)) {
            return response.results;
          } else if ('data' in response) {
            const data = response.data;
            if (Array.isArray(data)) {
              return data;
            } else if (data && typeof data === 'object' && 'results' in data && Array.isArray(data.results)) {
              return data.results;
            }
          }
        }
        
        console.warn('Unexpected GBP profiles response format:', response);
        return [];
      } catch (error) {
        console.error('Error fetching GBP profiles:', error);
        return [];
      }
    },
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  const connectGoogle = async () => {
    try {
      const response = await api.get<{ oauth_url: string }>('/api/marketing/gbp-profiles/connect/');
      if (response.data?.oauth_url) window.location.href = response.data.oauth_url;
    } catch {
      setErrorMessage('Failed to connect to Google Business Profile. Please try again.');
    }
  };

  const retryConnection = useMutation({
    mutationFn: (profileId: number) => api.post(`/api/marketing/gbp-profiles/${profileId}/sync-from-google/`, {}),
    onSuccess: () => {
      setErrorMessage(null);
      setSuccessMessage('Business location linked and profile synced from Google.');
      queryClient.invalidateQueries({ queryKey: ['gbp-profiles'] });
    },
    onError: (error: { responseData?: { error?: string } }) => {
      setSuccessMessage(null);
      setErrorMessage(error.responseData?.error || 'Unable to sync Google Business Profile. Please reconnect.');
    },
  });

  // Disconnect GBP profile mutation
  const disconnectProfileMutation = useMutation({
    mutationFn: async (profileId: number) => {
      return api.post(`/api/marketing/gbp-profiles/${profileId}/disconnect/`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gbp-profiles'] });
    }
  });

  const handleDisconnectProfile = (profileId: number) => {
    if (window.confirm('Are you sure you want to disconnect this Google Business Profile?')) {
      disconnectProfileMutation.mutate(profileId);
    }
  };

  const handleLocationSelectSuccess = () => {
    setSuccessMessage('Google Business Profile connected successfully!');
    queryClient.invalidateQueries({ queryKey: ['gbp-profiles'] });
  };

  return (
    <Box sx={{ width: '100%' }}>
      {successMessage && (
        <Alert
          color="success"
          startDecorator={<CheckIcon />}
          sx={{ mb: 3 }}
          endDecorator={
            <IconButton 
              variant="plain" 
              size="sm" 
              color="success"
              onClick={() => setSuccessMessage(null)}
            >
              <CloseIcon />
            </IconButton>
          }
        >
          {successMessage}
        </Alert>
      )}
      
      {errorMessage && (
        <Alert
          color="danger"
          startDecorator={<ErrorIcon />}
          sx={{ mb: 3 }}
          endDecorator={
            <IconButton 
              variant="plain" 
              size="sm" 
              color="danger"
              onClick={() => setErrorMessage(null)}
            >
              <CloseIcon />
            </IconButton>
          }
        >
          {errorMessage}
        </Alert>
      )}

      {/* Google Business Profile Section */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="h3" sx={{ mb: 2 }}>
            <GoogleIcon style={{ marginRight: 8 }} />
            Google Business Profile Integration
          </Typography>
          
          <Typography level="body-md" sx={{ mb: 3, color: 'text.secondary' }}>
            Connect your Google Business Profile to manage posts, track reviews, and monitor your online presence.
          </Typography>

          {profilesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : Array.isArray(profiles) && profiles.length > 0 ? (
            <Stack spacing={2}>
              {profiles.map((profile: GBPProfile) => (
                <Card key={profile.id} variant="soft" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography level="title-md">{profile.location_name}</Typography>
                        <Chip
                          size="sm"
                          color={profile.connection_status === 'location_required' ? 'warning' : 'neutral'}
                          startDecorator={<CheckIcon />}
                        >
                          {profile.connection_status === 'location_required' ? 'Location not linked' : 'Location linked'}
                        </Chip>
                      </Box>
                      <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        {profile.address}
                      </Typography>
                      <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        {profile.phone}
                      </Typography>
                      {profile.website && (
                        <Typography level="body-sm">
                          <a href={profile.website} target="_blank" rel="noopener noreferrer">
                            {profile.website}
                          </a>
                        </Typography>
                      )}
                      {profile.connection_status !== 'location_required' && profile.last_synced && (
                        <Typography level="body-xs" sx={{ mt: 1, color: 'text.tertiary' }}>
                          Last synced: {new Date(profile.last_synced).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      color="danger"
                      size="sm"
                      startDecorator={<LinkOffIcon />}
                      onClick={() => handleDisconnectProfile(profile.id)}
                      loading={disconnectProfileMutation.isPending}
                    >
                      Disconnect
                    </Button>
                  </Box>
                  {profile.connection_status === 'location_required' && (
                    <Alert color="warning">Google authorization is saved, but the business location still needs to be linked.</Alert>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    {profile.connection_status === 'location_required' && (
                      <Button onClick={() => retryConnection.mutate(profile.id)} loading={retryConnection.isPending}>
                        Retry connection
                      </Button>
                    )}
                    <Button variant="outlined" onClick={connectGoogle}>Reconnect Google</Button>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography level="body-lg" sx={{ mb: 2 }}>
                No Google Business Profiles connected
              </Typography>
              <Button
                variant="solid"
                color="primary"
                size="lg"
                startDecorator={<LinkIcon />}
                onClick={connectGoogle}
              >
                Connect Google Business Profile
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ my: 4 }} />

      {/* Keywords Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SearchIcon style={{ marginRight: 8 }} color="var(--joy-palette-text-secondary)" />
          <Typography level="h3">SEO Keywords</Typography>
        </Box>
        <Card variant="outlined">
          <CardContent>
            <Typography level="body-md" sx={{ mb: 3, color: 'text.secondary' }}>
              Track your keyword rankings and monitor your SEO performance for local searches.
            </Typography>
            <KeywordsManagement />
          </CardContent>
        </Card>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Competitors Section */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <BusinessIcon style={{ marginRight: 8 }} color="var(--joy-palette-text-secondary)" />
          <Typography level="h3">Competitor Tracking</Typography>
        </Box>
        <Card variant="outlined">
          <CardContent>
            <Typography level="body-md" sx={{ mb: 3, color: 'text.secondary' }}>
              Monitor your competitors' online presence and track their SEO metrics.
            </Typography>
            <CompetitorsManagement />
          </CardContent>
        </Card>
      </Box>

      {/* GBP Location Selection Modal */}
      <GBPLocationSelectModal
        open={showLocationSelectModal}
        onClose={() => setShowLocationSelectModal(false)}
        onSuccess={handleLocationSelectSuccess}
      />
    </Box>
  );
};